/**
 * Magic-Link Consumer Page
 *
 * Consumes magic-link token and redirects to dashboard
 */

import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import { MagicLinkResponseSchema } from "@/lib/api/schemas";

export default function MagicLinkPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setAccount = useAuthStore((s) => s.setAccount);
  const setAuthError = useAuthStore((s) => s.setError);

  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const consumeMagicLink = async () => {
      try {
        const token = searchParams.get("token");
        const redirect = searchParams.get("redirect") || "/dashboard";

        if (!token) {
          setStatus("error");
          setErrorMessage("No magic-link token provided");
          return;
        }

        // Consume the magic-link and get tokens
        const response = await apiClient.get(
          `/v1/auth/magic-link?token=${encodeURIComponent(token)}&redirect=${encodeURIComponent(redirect)}`
        );

        const data = MagicLinkResponseSchema.parse(response);

        // Store tokens
        localStorage.setItem("access_token", data.accessToken);
        localStorage.setItem("refresh_token", data.refreshToken);

        // Set account
        setAccount({
          id: "",
          email: "",
          fullName: "",
          role: "seller",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });

        setStatus("success");

        // Redirect after a short delay
        setTimeout(() => {
          navigate(redirect);
        }, 1500);
      } catch (error: any) {
        console.error("Magic-link consumption failed:", error);
        setStatus("error");
        setErrorMessage(
          error.message || "Failed to process magic-link. Please try signing in again."
        );
        setAuthError(error.message || "Authentication failed");
      }
    };

    consumeMagicLink();
  }, [searchParams, navigate, setAccount, setAuthError]);

  return (
    <div className="bg-white rounded-lg shadow-xl p-8 text-center min-h-96 flex flex-col items-center justify-center max-w-md w-full">
      {status === "loading" && (
        <div>
          <div className="mb-6">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-ink mb-2">Signing You In...</h1>
          <p className="text-slate-600">Please wait while we verify your link</p>
        </div>
      )}

      {status === "success" && (
        <div>
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
              <span className="text-4xl">✓</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-ink mb-2">Welcome!</h1>
          <p className="text-slate-600 mb-6">
            You&apos;re now signed in. Redirecting to your dashboard...
          </p>
          <div className="inline-block">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
          </div>
        </div>
      )}

      {status === "error" && (
        <div>
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full">
              <span className="text-4xl">✕</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-ink mb-2">Sign In Failed</h1>
          <p className="text-red-600 mb-6">{errorMessage}</p>
          <div className="space-y-3">
            <p className="text-slate-600 text-sm">The link may have expired or be invalid.</p>
            <Link
              to="/auth/login"
              className="inline-block px-8 py-3 bg-accent text-white rounded-lg hover:bg-accent-dark transition font-semibold"
            >
              Try Signing In Again
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
